/**
 * Client-side GET cache for per-point API calls (งาน #1695 — ลดอาการ "กดจุดเดิมซ้ำแล้วยิงใหม่").
 *
 * Why: the summary card fires many /api/* GETs keyed purely by lat/lon, and several
 * ask for the SAME point within one analysis (e.g. /api/gistda/flood is hit by
 * loadFlood + the advice path + the basin banner). The server cache is in-memory per
 * Function instance and is wiped on cold start, so repeats still cost a round-trip.
 * This memoises the in-flight / recent Response per URL on the browser so:
 *   - identical calls in the same analysis share ONE network request (in-flight dedup)
 *   - re-tapping the same point within the TTL serves from memory, no request at all
 *
 * No data is invented or altered — we only hand back a clone of the real Response.
 * Non-OK / rejected fetches are never pinned, so a transient failure retries next tap.
 */
type Entry = { at: number; p: Promise<Response> };
const store = new Map<string, Entry>();
const DEFAULT_TTL_MS = 90_000; // ~1.5 นาที — long enough to cover a re-tap, short enough to stay fresh
const MAX_ENTRIES = 200;

export function cachedFetch(url: string, ttlMs: number = DEFAULT_TTL_MS): Promise<Response> {
  const now = Date.now();
  const hit = store.get(url);
  if (hit && now - hit.at < ttlMs) return hit.p.then((r) => r.clone());
  const p = fetch(url);
  // don't pin a failure or a non-OK response — drop it so the next tap retries the source
  p.then((r) => { if (!r.ok) store.delete(url); }, () => store.delete(url));
  store.set(url, { at: now, p });
  if (store.size > MAX_ENTRIES) store.delete(store.keys().next().value!); // oldest first
  return p.then((r) => r.clone());
}
